import { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createPortal } from 'react-dom';
import { api } from '../../lib/api.js';
import { toast } from '../../lib/toast.js';
import { SearchableSelect } from '../primitives/SearchableSelect.js';
import { computeEndDate, DEPLOY_DURATIONS } from '@cls/shared';
import { useReferenceList } from '../../features/reference/useReferenceList.js';

interface Worker { id: string; name: string; role: string; }
interface Project { id: string; code: string; name: string; status: string; }
interface Contact { id: string; name: string; role: string | null; customer: string | null; }

interface Props {
  onClose: () => void;
  deploymentId: string;
  initial: {
    workerId: string;
    projectId: string;
    startDate: string;
    startTime: string | null;
    duration: string;
    endDate: string | null;
    status: string;
    supervisorId: string | null;
    meet: string | null;
    notes: string | null;
    waMessageSent?: boolean;
    requesterId?: string | null;
  };
}

export function EditDeploymentModal({ onClose, deploymentId, initial }: Props) {
  const qc = useQueryClient();
  const [workerId, setWorkerId] = useState(initial.workerId);
  const [projectId, setProjectId] = useState(initial.projectId);
  const [startDate, setStartDate] = useState(initial.startDate.slice(0, 10));
  const [startTime, setStartTime] = useState(initial.startTime ?? '07:30');
  const [duration, setDuration] = useState(initial.duration);
  const { values: durations } = useReferenceList('durations', DEPLOY_DURATIONS);
  const durationOptions = durations.includes(duration) ? durations : [duration, ...durations];
  const [endDate, setEndDate] = useState((initial.endDate ?? '').slice(0, 10));
  const [status, setStatus] = useState(initial.status);
  const [supervisorId, setSupervisorId] = useState(initial.supervisorId ?? '');
  const [meet, setMeet] = useState(initial.meet ?? '');
  const [waSent, setWaSent] = useState<'yes' | 'no'>(initial.waMessageSent ? 'yes' : 'no');
  const [requesterId, setRequesterId] = useState(initial.requesterId ?? '');
  const [notes, setNotes] = useState(initial.notes ?? '');

  // End Date auto-fills from a fixed duration; for "Until project end" it clears so
  // the server falls back to the project's end date. Ops can always type an override.
  function applyDuration(v: string) {
    setDuration(v);
    setEndDate(computeEndDate(startDate, v, null) ?? '');
  }
  function applyStartDate(v: string) {
    setStartDate(v);
    const auto = computeEndDate(v, duration, null);
    if (auto) setEndDate(auto);
  }

  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const { data: workers = [] } = useQuery({ queryKey: ['workers'], queryFn: () => api.get<Worker[]>('/workers').then((r) => r.data) });
  const { data: projects = [] } = useQuery({ queryKey: ['projects'], queryFn: () => api.get<Project[]>('/projects').then((r) => r.data) });
  const { data: contacts = [] } = useQuery({ queryKey: ['contacts'], queryFn: () => api.get<Contact[]>('/contacts').then((r) => r.data) });

  const save = useMutation({
    mutationFn: async () => api.patch(`/deployments/${deploymentId}`, {
      workerId,
      projectId,
      startDate,
      startTime: startTime || null,
      duration,
      endDate: endDate || null,
      status,
      supervisorId: supervisorId || null,
      meet: meet.trim() || null,
      requesterId: requesterId || null,
      notes: notes.trim() || null,
      waMessageSent: waSent === 'yes',
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['deployments'] });
      qc.invalidateQueries({ queryKey: ['deployments', deploymentId] });
      qc.invalidateQueries({ queryKey: ['workers'] });
      toast.success('Deployment updated');
      onClose();
    },
    onError: (e: any) => toast.error('Save failed', e?.response?.data?.message ?? 'Check fields'),
  });

  return createPortal(
    <div className="mo open" onClick={onClose}>
      <div className="modal" style={{ width: 600 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-title" id="ml-title">✏ Edit Deployment Record — {deploymentId.slice(0, 8)}</div>
        <div className="fg2">
          <div className="form-row">
            <label className="form-label">Worker</label>
            <SearchableSelect
              id="ml-worker"
              value={workerId}
              onChange={setWorkerId}
              placeholder="— Select worker —"
              options={workers.map((w) => ({ value: w.id, label: `${w.name} — ${w.role}` }))}
            />
          </div>
          <div className="form-row">
            <label className="form-label">Project</label>
            <SearchableSelect
              id="ml-project"
              value={projectId}
              onChange={setProjectId}
              placeholder="— Select project —"
              options={projects.map((p) => ({ value: p.id, label: `${p.code} — ${p.name}` }))}
            />
          </div>
        </div>
        <div className="fg2">
          <div className="form-row">
            <label className="form-label">Start Date</label>
            <input type="date" className="form-input" id="ml-date" value={startDate} onChange={(e) => applyStartDate(e.target.value)} />
          </div>
          <div className="form-row">
            <label className="form-label">Start Time</label>
            <input type="time" className="form-input" id="ml-time" value={startTime} onChange={(e) => setStartTime(e.target.value)} />
          </div>
        </div>
        <div className="fg2">
          <div className="form-row">
            <label className="form-label">Duration</label>
            <select className="form-select" id="ml-duration" value={duration} onChange={(e) => applyDuration(e.target.value)}>
              {durationOptions.map((d) => <option key={d}>{d}</option>)}
            </select>
          </div>
          <div className="form-row">
            <label className="form-label">Status</label>
            <select className="form-select" id="ml-status" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="active">Active</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
              {status === 'sick' ? <option value="sick">Sick</option> : null}
              {status === 'holiday' ? <option value="holiday">Holiday</option> : null}
            </select>
          </div>
        </div>
        <div className="form-row">
          <label className="form-label">End Date</label>
          <input type="date" className="form-input" id="ml-enddate" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 4 }}>
            Auto-fills from the duration. Set this to record the exact date the worker finished on site — e.g. when correcting a completed deployment.
          </div>
        </div>
        <div className="fg2">
          <div className="form-row">
            <label className="form-label">Supervisor</label>
            <SearchableSelect
              id="ml-sup"
              value={supervisorId}
              onChange={setSupervisorId}
              placeholder="— Select supervisor —"
              options={contacts.map((c) => ({ value: c.id, label: `${c.name}${c.customer ? ` (${c.customer})` : ''}${c.role ? ` — ${c.role}` : ''}` }))}
            />
          </div>
          <div className="form-row">
            <label className="form-label">Who to Meet</label>
            <input type="text" className="form-input" id="ml-meet" value={meet} onChange={(e) => setMeet(e.target.value)} />
          </div>
        </div>
        <div className="form-row">
          <label className="form-label">WhatsApp Sent?</label>
          <div style={{ display: 'flex', gap: 14, marginTop: 4 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, cursor: 'pointer' }}>
              <input type="radio" name="ml-wa" value="yes" checked={waSent === 'yes'} onChange={() => setWaSent('yes')} style={{ accentColor: 'var(--wa)' }} /> Yes
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, cursor: 'pointer' }}>
              <input type="radio" name="ml-wa" value="no" checked={waSent === 'no'} onChange={() => setWaSent('no')} style={{ accentColor: 'var(--wa)' }} /> No
            </label>
          </div>
        </div>
        <div className="form-row">
          <label className="form-label">Requested By</label>
          <SearchableSelect
            id="ml-requester"
            value={requesterId}
            onChange={setRequesterId}
            placeholder="— No requester —"
            options={contacts.map((c) => ({ value: c.id, label: `${c.name}${c.customer ? ` (${c.customer})` : ''}${c.role ? ` — ${c.role}` : ''}` }))}
          />
        </div>
        <div className="form-row">
          <label className="form-label">Notes</label>
          <textarea className="form-textarea" id="ml-notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
        </div>
        <div className="modal-actions">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" id="ml-save-btn" onClick={() => save.mutate()} disabled={save.isPending || !workerId || !projectId || !startDate}>
            {save.isPending ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
